import React, { useEffect, useState } from 'react';
import { Loader2, LinkIcon, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';

export default function EbayConnectPanel({ settings, onConnected }) {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState(null);
  const { toast } = useToast();

  const connect = async () => {
    setLoading(true);
    try {
      const res = await base44.functions.invoke('ebayAuthStart', {});
      const link = res.data?.url;
      if (!link) throw new Error('No consent URL returned');
      setUrl(link);
      window.open(link, '_blank');
      toast({ description: 'Complete the sign-in on the eBay tab, then come back and refresh.' });
    } catch (e) {
      toast({ variant: 'destructive', description: e.message || 'Failed to start eBay connection.' });
    } finally {
      setLoading(false);
    }
  };

  const connected = settings?.connected;

  return (
    <div className="bg-[#111115] border border-white/10 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-white uppercase tracking-wider">Connection</h2>
          <p className="text-xs text-white/50 mt-1">
            Authorise your eBay seller account so the site can list products and pull orders.
          </p>
        </div>
        <span
          className={`text-xs px-2.5 py-1 border ${
            connected
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
              : 'bg-white/5 border-white/10 text-white/40'
          }`}
        >
          {connected ? 'CONNECTED' : 'NOT CONNECTED'}
        </span>
      </div>

      {connected && settings?.seller_username && (
        <div className="mt-4 flex items-center gap-2 text-sm text-white/70">
          <span className="text-white/40">Seller:</span>
          <span className="text-white font-medium">{settings.seller_username}</span>
        </div>
      )}

      {connected && settings?.refresh_token_expires_at && (
        <div className="mt-1 text-xs text-white/40">
          Token valid until {new Date(settings.refresh_token_expires_at).toLocaleDateString('en-GB')}
        </div>
      )}

      {url && !connected && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="mt-4 flex items-center gap-2 text-xs text-primary hover:underline"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Re-open eBay sign-in tab
        </a>
      )}

      <Button
        onClick={connect}
        disabled={loading}
        className="mt-5 w-full bg-primary hover:bg-primary/90"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
        {connected ? 'Reconnect account' : 'Connect eBay account'}
      </Button>

      {settings?.last_error && (
        <div className="mt-4 flex items-start gap-2 text-xs text-red-400 bg-red-500/5 border border-red-500/20 p-3">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{settings.last_error}</span>
        </div>
      )}
    </div>
  );
}